import {
  applyAlertFilters,
  ARCHITECTURE_ALERT_FILTER_OPTIONS,
  JOB_ARCHITECTURE_ALERTS_FILTER_ID,
} from '@/components/jobArchitecture/alertHelpers';
import { familyNodeId, groupId } from '@/components/jobArchitecture/helpers';

export { JOB_ARCHITECTURE_ALERTS_FILTER_ID };

export const JOB_ARCHITECTURE_GROUP_FILTER_ID = 'job_architecture_group';
export const JOB_ARCHITECTURE_FAMILY_FILTER_ID = 'job_architecture_family';
export const JOB_ARCHITECTURE_PROFILE_FILTER_ID = 'job_architecture_profile';
export const JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID = 'job_architecture_job_level';
export const JOB_ARCHITECTURE_JOB_CODE_FILTER_ID = 'job_architecture_job_code';

export const JOB_ARCHITECTURE_FILTER_QUERY_KEYS = [
  JOB_ARCHITECTURE_GROUP_FILTER_ID,
  JOB_ARCHITECTURE_FAMILY_FILTER_ID,
  JOB_ARCHITECTURE_PROFILE_FILTER_ID,
  JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID,
  JOB_ARCHITECTURE_JOB_CODE_FILTER_ID,
  JOB_ARCHITECTURE_ALERTS_FILTER_ID,
];

export const mergeJobArchitectureRouteQuery = (routeQuery, mergedTempFilters, searchText) => {
  const next = { ...routeQuery };

  JOB_ARCHITECTURE_FILTER_QUERY_KEYS.forEach((key) => {
    delete next[key];
  });

  delete next.q;

  const result = { ...next, ...mergedTempFilters };

  if (searchText) {
    result.q = searchText;
  }

  return result;
};

// Narrow sidebar dropdown options to what's in the table and keep applied selections visible.
export const mergeFilterFieldOptions = (currentFields, optionsForVisibleTable) => currentFields.map((field) => {
  if (field.id === JOB_ARCHITECTURE_ALERTS_FILTER_ID) {
    return {
      ...field,
      options: ARCHITECTURE_ALERT_FILTER_OPTIONS,
      value: field.value,
    };
  }

  const tableOptionsField = optionsForVisibleTable.find(
    (visibleTableField) => visibleTableField.id === field.id,
  );

  if (!tableOptionsField) {
    return field;
  }

  const optionMap = new Map(tableOptionsField.options.map((option) => [option.id, option]));

  field.value.forEach((selected) => {
    if (!optionMap.has(selected.id)) {
      optionMap.set(selected.id, selected);
    }
  });

  const options = [...optionMap.values()].sort((a, b) => a.label.localeCompare(b.label));

  return {
    ...field,
    options,
    value: field.value,
  };
});

// Restore applied filter selections from a snapshot after a compare navigation.
export const applyFilterSnapshotToFields = (filterFields, snapshotFields) => filterFields.map((field) => {
  const savedField = snapshotFields.find(
    (snapshotField) => snapshotField.id === field.id,
  );

  if (!savedField?.value?.length) {
    return field;
  }

  const optionById = new Map(field.options.map((option) => [option.id, option]));
  const value = savedField.value.map(
    (savedOption) => optionById.get(savedOption.id) || savedOption,
  );

  return { ...field, value };
});

export const applySearchFilter = (tree, searchText) => {
  const q = (searchText || '').trim();

  if (!q || !tree?.groups) {
    return tree;
  }

  const searchTerm = q.toLowerCase();
  const newGroups = [];

  tree.groups.forEach((group) => {
    const families = group.families || [];
    const groupName = group.name || '';

    if (groupName.toLowerCase().includes(searchTerm)) {
      newGroups.push({
        ...group,
        families: families.map((family) => ({
          ...family,
          profiles: family.profiles || [],
        })),
      });

      return;
    }

    const newFamilies = [];

    families.forEach((family) => {
      const profiles = family.profiles || [];
      const familyName = family.name || '';

      if (familyName.toLowerCase().includes(searchTerm)) {
        newFamilies.push({
          ...family,
          profiles,
        });

        return;
      }

      const filteredProfiles = profiles.filter((profile) => (
        (profile?.title || '').toLowerCase().includes(searchTerm)
      ));

      if (filteredProfiles.length > 0) {
        newFamilies.push({ ...family, profiles: filteredProfiles });
      }
    });

    if (newFamilies.length > 0) {
      newGroups.push({ ...group, families: newFamilies });
    }
  });

  return { ...tree, groups: newGroups };
};

// Build filter options from architecture tree for client-side filtering.
export const deriveFilterFields = (tree) => {
  const seenGroups = new Set();
  const groupOptions = [];

  const seenFamilies = new Set();
  const familyOptions = [];

  const seenProfiles = new Set();
  const profileOptions = [];

  const seenJobLevels = new Set();
  const jobLevelOptions = [];

  const seenJobCodes = new Set();
  const jobCodeOptions = [];

  (tree?.groups || []).forEach((group) => {
    const gid = groupId(group);

    if (!seenGroups.has(gid)) {
      seenGroups.add(gid);
      groupOptions.push({ id: gid, label: group.name || 'Uncategorized' });
    }

    (group.families || []).forEach((family) => {
      const fid = familyNodeId(group, family);

      if (!seenFamilies.has(fid)) {
        seenFamilies.add(fid);
        familyOptions.push({ id: fid, label: family.name || 'Uncategorized' });
      }

      (family.profiles || []).forEach((profile) => {
        const {
          job_level: jobLevel,
          hris_job_code: hrisJobCode,
          ulid,
          title,
        } = profile;

        if (ulid && title && !seenProfiles.has(ulid)) {
          seenProfiles.add(ulid);
          profileOptions.push({ id: ulid, label: title });
        }

        if (jobLevel && !seenJobLevels.has(jobLevel)) {
          seenJobLevels.add(jobLevel);
          jobLevelOptions.push({ id: jobLevel, label: jobLevel });
        }

        if (hrisJobCode && !seenJobCodes.has(hrisJobCode)) {
          seenJobCodes.add(hrisJobCode);
          jobCodeOptions.push({ id: hrisJobCode, label: hrisJobCode });
        }
      });
    });
  });

  groupOptions.sort((a, b) => a.label.localeCompare(b.label));
  familyOptions.sort((a, b) => a.label.localeCompare(b.label));
  profileOptions.sort((a, b) => a.label.localeCompare(b.label));
  jobLevelOptions.sort((a, b) => a.label.localeCompare(b.label));
  jobCodeOptions.sort((a, b) => a.label.localeCompare(b.label));

  return [
    {
      id: JOB_ARCHITECTURE_GROUP_FILTER_ID,
      label: 'Job Family Group',
      options: groupOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_FAMILY_FILTER_ID,
      label: 'Job Family',
      options: familyOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_PROFILE_FILTER_ID,
      label: 'Job Profile',
      options: profileOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID,
      label: 'Job Level',
      options: jobLevelOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_JOB_CODE_FILTER_ID,
      label: 'Job Code',
      options: jobCodeOptions,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
    {
      id: JOB_ARCHITECTURE_ALERTS_FILTER_ID,
      label: 'Architecture Alerts',
      options: ARCHITECTURE_ALERT_FILTER_OPTIONS,
      value: [],
      alwaysOn: true,
      category: 'basic',
    },
  ];
};

// Prune tree by selected groups / families / profiles / job levels / job codes, removing empty nodes.
export const applyClientFilters = (
  tree,
  selectedGroups,
  selectedFamilies,
  selectedProfiles,
  selectedJobLevels,
  selectedJobCodes,
) => {
  if (!tree?.groups) {
    return tree;
  }

  const hasGroupFilter = selectedGroups.length > 0;
  const hasFamilyFilter = selectedFamilies.length > 0;
  const hasProfileFilter = selectedProfiles.length > 0;
  const hasLevelFilter = selectedJobLevels.length > 0;
  const hasJobCodeFilter = selectedJobCodes.length > 0;

  if (
    !hasGroupFilter
    && !hasFamilyFilter
    && !hasProfileFilter
    && !hasLevelFilter
    && !hasJobCodeFilter
  ) {
    return tree;
  }

  const groupSet = new Set(selectedGroups);
  const familySet = new Set(selectedFamilies);
  const profileSet = new Set(selectedProfiles);
  const levelSet = new Set(selectedJobLevels);
  const jobCodeSet = new Set(selectedJobCodes);

  const newGroups = [];

  tree.groups.forEach((group) => {
    if (hasGroupFilter && !groupSet.has(groupId(group))) {
      return;
    }

    const newFamilies = [];

    (group.families || []).forEach((family) => {
      if (hasFamilyFilter && !familySet.has(familyNodeId(group, family))) {
        return;
      }

      const filteredProfiles = (family.profiles || []).filter((profile) => {
        if (hasProfileFilter && !profileSet.has(profile.ulid)) return false;
        if (hasLevelFilter && !levelSet.has(profile.job_level)) return false;
        if (hasJobCodeFilter && !jobCodeSet.has(profile.hris_job_code)) return false;

        return true;
      });

      if (filteredProfiles.length > 0) {
        newFamilies.push({ ...family, profiles: filteredProfiles });
      }
    });

    if (newFamilies.length > 0) {
      newGroups.push({ ...group, families: newFamilies });
    }
  });

  return { ...tree, groups: newGroups };
};

export const getSelectedAlertCategoryIds = (filterFields) => {
  const alertFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_ALERTS_FILTER_ID);

  return alertFilter?.value?.map((value) => value.id) || [];
};

export const buildFilteredTree = (tree, filterFields, searchText) => {
  if (!tree) {
    return null;
  }

  const groupFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_GROUP_FILTER_ID);
  const familyFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_FAMILY_FILTER_ID);
  const profileFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_PROFILE_FILTER_ID);
  const levelFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_JOB_LEVEL_FILTER_ID);
  const jobCodeFilter = filterFields.find((field) => field.id === JOB_ARCHITECTURE_JOB_CODE_FILTER_ID);

  const groupIds = groupFilter?.value?.map((value) => value.id) || [];
  const familyIds = familyFilter?.value?.map((value) => value.id) || [];
  const profileIds = profileFilter?.value?.map((value) => value.id) || [];
  const levelIds = levelFilter?.value?.map((value) => value.id) || [];
  const jobCodeIds = jobCodeFilter?.value?.map((value) => value.id) || [];
  const alertCategoryIds = getSelectedAlertCategoryIds(filterFields);

  const afterClientFilters = applyClientFilters(
    tree,
    groupIds,
    familyIds,
    profileIds,
    levelIds,
    jobCodeIds,
  );

  const afterAlertFilters = applyAlertFilters(afterClientFilters, alertCategoryIds);

  return applySearchFilter(afterAlertFilters, searchText);
};
